import React, { useEffect, useState, useRef } from 'react';
interface RangeSliderProps {
  min: number;
  max: number;
  step?: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatLabel?: (value: number) => string;
}
export function RangeSlider({
  min,
  max,
  step = 1,
  value,
  onChange,
  formatLabel = v => v.toString()
}: RangeSliderProps) {
  const [minVal, maxVal] = value;
  const [isDraggingMin, setIsDraggingMin] = useState(false);
  const [isDraggingMax, setIsDraggingMax] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const getPercentage = (val: number) => (val - min) / (max - min) * 100;
  const handleMouseDown = (isMin: boolean) => (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (isMin) {
      setIsDraggingMin(true);
    } else {
      setIsDraggingMax(true);
    }
  };
  const updateValue = (clientX: number, isMin: boolean) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    let newValue = min + percentage * (max - min);
    // Round to nearest step
    newValue = Math.round(newValue / step) * step;
    if (isMin) {
      // Ensure min doesn't exceed max
      const newMin = Math.min(newValue, maxVal - step);
      onChange([Math.max(min, newMin), maxVal]);
    } else {
      // Ensure max doesn't go below min
      const newMax = Math.max(newValue, minVal + step);
      onChange([minVal, Math.min(max, newMax)]);
    }
  };
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      if (isDraggingMin) {
        updateValue(clientX, true);
      } else if (isDraggingMax) {
        updateValue(clientX, false);
      }
    };
    const handleMouseUp = () => {
      setIsDraggingMin(false);
      setIsDraggingMax(false);
    };
    if (isDraggingMin || isDraggingMax) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove);
      document.addEventListener('touchend', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDraggingMin, isDraggingMax, minVal, maxVal]);
  const minPercentage = getPercentage(minVal);
  const maxPercentage = getPercentage(maxVal);
  return <div className="relative pt-2 pb-2" data-id="element-433">
      {/* Track */}
      <div ref={trackRef} className="relative h-2 bg-sand rounded-full cursor-pointer" data-id="element-434">
        {/* Active range */}
        <div className="absolute h-full bg-dark-green rounded-full" style={{
        left: `${minPercentage}%`,
        right: `${100 - maxPercentage}%`
      }} data-id="element-435" />

        {/* Min handle */}
        <div onMouseDown={handleMouseDown(true)} onTouchStart={handleMouseDown(true)} className={`absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-2 border-dark-green rounded-full cursor-grab shadow-md transition-transform ${isDraggingMin ? 'scale-110 cursor-grabbing' : 'hover:scale-110'}`} style={{
        left: `${minPercentage}%`,
        transform: `translate(-50%, -50%)`
      }} data-id="element-436">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-charcoal text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-437">
            {formatLabel(minVal)}
          </div>
        </div>

        {/* Max handle */}
        <div onMouseDown={handleMouseDown(false)} onTouchStart={handleMouseDown(false)} className={`absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white border-2 border-dark-green rounded-full cursor-grab shadow-md transition-transform ${isDraggingMax ? 'scale-110 cursor-grabbing' : 'hover:scale-110'}`} style={{
        left: `${maxPercentage}%`,
        transform: `translate(-50%, -50%)`
      }} data-id="element-438">
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-charcoal text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-439">
            {formatLabel(maxVal)}
          </div>
        </div>
      </div>
    </div>;
}